export const PROJECT_STATUSES = ['active', 'paused', 'completed', 'archived'] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

/** İç projede bitiş tarihi isteğe bağlıdır; müşteriye özel projede zorunludur ve görev vadeleri onu aşamaz. */
export const PROJECT_KINDS = ['internal', 'custom'] as const
export type ProjectKind = (typeof PROJECT_KINDS)[number]

export const PROJECT_ICONS = ['monitor', 'users', 'calendar', 'stethoscope', 'store', 'utensils', 'package'] as const
export type ProjectIconKey = (typeof PROJECT_ICONS)[number]

export const PROJECT_COLORS = ['navy', 'blue', 'violet', 'orange', 'green'] as const
export type ProjectColor = (typeof PROJECT_COLORS)[number]

export const DEPLOYMENT_TYPES = ['customer_server', 'cloud', 'hybrid'] as const
export type DeploymentType = (typeof DEPLOYMENT_TYPES)[number]

export const DISCIPLINES = ['management', 'frontend', 'backend', 'qa', 'installation', 'design', 'sales'] as const
export type Discipline = (typeof DISCIPLINES)[number]

export const TASK_TYPES = ['development', 'installation', 'demo'] as const
export type TaskType = (typeof TASK_TYPES)[number]

/** İş akışı sırası: Yapılacak → Başlandı → İncelemede → Onayda → Tamamlandı. */
export const TASK_STATUSES = ['todo', 'in_progress', 'review', 'approval', 'done'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const
export type Priority = (typeof PRIORITIES)[number]

export const COMMENT_VISIBILITIES = ['internal', 'shared'] as const
export type CommentVisibility = (typeof COMMENT_VISIBILITIES)[number]

export const INSTALLATION_KINDS = ['installation', 'demo'] as const
export type InstallationKind = (typeof INSTALLATION_KINDS)[number]

export const INSTALLATION_STATUSES = ['planned', 'in_progress', 'blocked', 'awaiting_approval', 'done'] as const
export type InstallationStatus = (typeof INSTALLATION_STATUSES)[number]

export const STAGE_STATUSES = ['pending', 'in_progress', 'done'] as const
export type StageStatus = (typeof STAGE_STATUSES)[number]

export const SEVERITIES = ['low', 'medium', 'high'] as const
export type Severity = (typeof SEVERITIES)[number]

export const DOC_STATUSES = ['signed', 'to_sign', 'waiting'] as const
export type DocStatus = (typeof DOC_STATUSES)[number]

export const MODULE_REQUIREMENTS = ['required', 'optional'] as const
export type ModuleRequirement = (typeof MODULE_REQUIREMENTS)[number]

export const PRICING_TYPES = ['one_time', 'monthly', 'yearly'] as const
export type PricingType = (typeof PRICING_TYPES)[number]

export const RELEASE_CHANNELS = ['stable', 'beta'] as const
export type ReleaseChannel = (typeof RELEASE_CHANNELS)[number]

export type StaffUser = { id: string; name: string; position: string }

export type TeamMember = { id: string; userId: string; discipline: Discipline; title: string; joinedAt: string }

/** Madde ayrı bir kişiye (vadeli) atanabilir; doneBy tamamlayan. */
export type ChecklistItem = { id: string; text: string; done: boolean; assigneeId: string | null; dueDate: string; doneBy: string | null }

export type TaskComment = { id: string; author: string; body: string; at: string; visibility: CommentVisibility }

/** Görev geçmişi türü (sunucu cp_task_events): created, status, assigned, unassigned, reviewer, sprint, due, priority, estimate,
 * title, archived, unarchived, comment, checklist, item_done, item_reopened, item_assigned, assignment_* … */
export type HistoryKind = string
/** userId kişi içeren olaylarda (atama, madde ataması, talep) ilgili kişi; detail ham değer; note gerekçe / madde adı. */
export type HistoryEntry = { id: string; at: string; by: string; kind: HistoryKind; status?: TaskStatus; detail?: string; userId?: string; note?: string }

export type Task = {
  id: string
  key: string
  title: string
  description: string
  type: TaskType
  status: TaskStatus
  priority: Priority
  assigneeId: string | null
  reviewerId: string | null
  watcherIds: string[]
  installationId: string | null
  parentId: string | null
  dependsOn: string[]
  sprint: string
  sprintId: string | null
  startDate: string
  dueDate: string
  estimateHours: number
  spentHours: number
  checklist: ChecklistItem[]
  comments: TaskComment[]
  history: HistoryEntry[]
  createdAt: string
}

export type Stage = {
  id: string
  name: string
  description: string
  ownerId: string | null
  plannedDate: string
  completion: number
  status: StageStatus
  taskIds: string[]
}

export type Blocker = { id: string; text: string; severity: Severity; ownerId: string | null; date: string; resolved: boolean }

export type CustomerDoc = { id: string; name: string; date: string; status: DocStatus }

export type Installation = {
  id: string
  /** CRM müşteri hesabı (kurulum işi hesaba bağlıdır). */
  accountId: string
  customer: string
  city: string
  kind: InstallationKind
  ownerId: string | null
  startDate: string
  targetDate: string
  status: InstallationStatus
  /** Sunucu hesaplar: aşama tamamlanma ortalaması. */
  progress: number
  stages: Stage[]
  blockers: Blocker[]
  documents: CustomerDoc[]
}

export type Note = { id: string; title: string; body: string; author: string; pinned: boolean; updatedAt: string }

export type ProjectFile = { id: string; name: string; size: number; mime: string; uploadedAt: string; uploadedBy: string; taskId: string | null }

export const MODULE_CATEGORIES = ['core', 'operations', 'integration', 'reporting', 'marketing'] as const
export type ModuleCategory = (typeof MODULE_CATEGORIES)[number]

/** Modülün içerdiği işlem/özellik; `group` ekranda başlık altında toplanır (örn. "Menü Yönetimi"). */
export type ModuleFeature = { id: string; group: string; name: string; description: string }

export type ModuleChangelogEntry = { id: string; version: string; date: string; notes: string }

/** Ortak katalogdaki modül; projelerden bağımsız tanımlanır, istenen projelere entegre edilir. */
export type CatalogModule = {
  id: string
  code: string
  name: string
  description: string
  category: ModuleCategory
  pricing: PricingType
  /** Liste fiyatı; decimal string (örn. "40000.00"). İstemcide hesaplanmaz. */
  price: string
  dependsOn: string[]
  active: boolean
  version: string
  /** Tahmini kurulum/devreye alma süresi (saat). */
  setupHours: number
  /** Deneme süresi (gün); 0 = deneme yok. */
  trialDays: number
  /** Teknik gereksinimler (donanım, entegrasyon, lisans vb.). */
  requirements: string[]
  features: ModuleFeature[]
  changelog: ModuleChangelogEntry[]
  updatedAt: string
}

/** Sunucu katalog listesinde hangi projelerin modülü kullandığını da döner. */
export type CatalogModuleView = CatalogModule & { usage: { projectId: string; requirement: ModuleRequirement; price: string; addedAt: string }[] }

export type Sector = { id: string; name: string; description: string; projectIds: string[] }
export type SectorInput = Omit<Sector, 'id'>

export type ProductPackage = {
  projectId: string
  requiredCount: number
  optionalCount: number
  base: Record<PricingType, string>
  addons: Record<PricingType, string>
}

/** Projeye entegrasyon kaydı: zorunluluk ve projeye özel fiyat. */
export type ModuleLink = { moduleId: string; requirement: ModuleRequirement; price: string; addedAt: string }

/** Proje detayında katalog + bağlantı birleşimi (salt okunur görünüm). */
export type ProjectModule = {
  id: string
  code: string
  name: string
  description: string
  category: ModuleCategory
  requirement: ModuleRequirement
  pricing: PricingType
  price: string
  dependsOn: string[]
}

export type Release = { id: string; version: string; channel: ReleaseChannel; releasedAt: string; notes: string }

/** Liste kartı: istatistikler sunucuda hesaplanır. */
export type ProjectSummary = {
  id: string
  key: string
  name: string
  description: string
  icon: ProjectIconKey
  color: ProjectColor
  status: ProjectStatus
  kind: ProjectKind
  /** catalog_sectors.id (proje oluştururken zorunlu). */
  sectorId: string
  deploymentType: DeploymentType | null
  startDate: string
  endDate: string
  version: string | null
  basePrice: Record<PricingType, string>
  moduleCount: number
  activeTasks: number
  /** Bitmemiş ve vadesi geçmiş görevler. */
  overdueTasks: number
  pendingApproval: number
  activeInstallations: number
  progress: number
  memberIds: string[]
}

export type Project = ProjectSummary & {
  createdAt: string
  updatedAt: string
  team: TeamMember[]
  tasks: Task[]
  installations: Installation[]
  notes: Note[]
  files: ProjectFile[]
  modules: ProjectModule[]
  releases: Release[]
}

export type ProjectInput = Pick<Project, 'key' | 'name' | 'description' | 'icon' | 'color' | 'status' | 'kind' | 'sectorId' | 'startDate' | 'endDate'>
export type TeamMemberInput = Omit<TeamMember, 'id' | 'joinedAt'>
export type TaskInput = Pick<
  Task,
  'title' | 'description' | 'type' | 'status' | 'priority' | 'assigneeId' | 'reviewerId' | 'installationId' | 'parentId' | 'sprintId' | 'startDate' | 'dueDate' | 'estimateHours' | 'spentHours'
>
export type InstallationInput = Pick<Installation, 'accountId' | 'kind' | 'ownerId' | 'startDate' | 'targetDate' | 'status'>
export type StageInput = Omit<Stage, 'id'>
export type BlockerInput = Omit<Blocker, 'id' | 'resolved'>
export type DocInput = Omit<CustomerDoc, 'id'>
export type NoteInput = Pick<Note, 'title' | 'body' | 'pinned'>
export type CatalogModuleInput = Omit<CatalogModule, 'id' | 'features' | 'changelog' | 'updatedAt'>
export type ModuleFeatureInput = Omit<ModuleFeature, 'id'>
export type ModuleChangelogInput = Omit<ModuleChangelogEntry, 'id'>
export type ModuleLinkInput = Pick<ModuleLink, 'requirement' | 'price'>
export type ReleaseInput = Omit<Release, 'id'>
